import { useContext } from "react";
import Interaction from "../../../../types/interaction/Interaction";
import { useOpenable } from "../../../../hooks/useOpenable";
import { Archive } from "@mui/icons-material";
import { AuthContext } from "../../../../context/AuthContext";
import { BulkExportModal } from "./BulkExportModal";
import { useTranslation } from "react-i18next";
import { FeatureFlagName } from "../../../../types/feature-flags/featureFlag";
import { FeatureFlagButton } from "../../../../components/common/FeatureFlags/FeatureFlagButton";

interface BulkExportTriggerProps {
  customerId: string | undefined;
  interactions: Interaction[];
}

function isValidSelectionCount(interactions: Interaction[]) {
  return interactions.length > 1 && interactions.length <= 1_000;
}

export function useBulkExportTrigger(interactions: Interaction[]) {
  const { user } = useContext(AuthContext);
  const hasPermissionToExport = user?.hasPermission("interactions:Export");

  function tooltipMessage() {
    if (!hasPermissionToExport) {
      return "You do not have permission to export interactions.";
    }
    if (!isValidSelectionCount(interactions)) {
      return "Select between 2 and 1,000 interactions to export.";
    }

    return "Export selected interactions.";
  }

  return {
    isDisabled: !hasPermissionToExport || !isValidSelectionCount(interactions),
    tooltipMessage,
  };
}

export function BulkExportTrigger({
  customerId,
  interactions,
}: Readonly<BulkExportTriggerProps>) {
  const { t } = useTranslation("common");
  const { isOpen, setIsOpen, toggleOpen } = useOpenable();
  const { isDisabled, tooltipMessage } = useBulkExportTrigger(interactions);

  return (
    <>
      <FeatureFlagButton
        featureFlag={FeatureFlagName.BulkExport}
        tooltip={tooltipMessage()}
        variant="outlined"
        onClick={toggleOpen}
        disabled={isDisabled}
        startIcon={<Archive />}
      >
        {t("common:bulkExport")}
      </FeatureFlagButton>
      <BulkExportModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        customerId={customerId}
        interactions={interactions}
      />
    </>
  );
}
